import '~/components/dob-input';

interface ValidationErrors {
  [fieldName: string]: string;
}

interface ApiResponse {
  success: boolean;
  errors?: ValidationErrors;
  message?: string;
}

function initTrialSignupForm(): void {
  const form = document.querySelector<HTMLFormElement>('.singshop-trial-signup-form');
  if (!form) return;

  // Progressive enhancement: add novalidate via JS so browser validation works without JS
  form.setAttribute('novalidate', '');

  // Generate form token with timestamp for spam protection
  const formLoadTime = Date.now();
  const formToken = btoa(JSON.stringify({ t: formLoadTime, r: Math.random().toString(36) }));
  const tokenField = form.querySelector<HTMLInputElement>('input[name="form_token"]');
  if (tokenField) {
    tokenField.value = formToken;
  }

  const errorSummary = document.getElementById('error-summary');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    // Clear previous errors
    clearErrors(form, errorSummary);

    const formData = new FormData(form);
    const actionUrl = form.getAttribute('action');
    const submitButton = form.querySelector<HTMLButtonElement>('.submit-button');

    if (!actionUrl) {
      console.error('Form action URL is missing');
      return;
    }

    // Disable button and show loader
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('submit-button--loading');
    }

    try {
      const response = await fetch(actionUrl, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        credentials: 'include',
      });

      const data: ApiResponse = await response.json();

      if (!response.ok || !data.success) {
        // Re-enable button on error
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('submit-button--loading');
        }

        if (data.errors) {
          // Check for spam detection - show generic error without revealing detection
          if (data.errors['_spam']) {
            if (errorSummary) {
              const spamMessage = form.dataset.errorSpamDetected ?? 'An error occurred. Please try again later.';
              errorSummary.textContent = spamMessage;
              errorSummary.classList.add('error-summary--visible');
              errorSummary.focus();
            }
            return;
          }
          displayErrors(form, data.errors, errorSummary);
        }
        return;
      }

      // Success - could redirect or show success message
      if (data.message) {
        showSuccessMessage(form, data.message);
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Re-enable button on error
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('submit-button--loading');
      }

      if (errorSummary) {
        const errorMessage = form.dataset.errorSubmissionFailed ?? 'An error occurred while submitting the form. Please try again.';
        errorSummary.textContent = errorMessage;
        errorSummary.classList.add('error-summary--visible');
        errorSummary.focus();
      }
    }
  });
}

function clearErrors(form: HTMLFormElement, errorSummary: HTMLElement | null): void {
  // Remove error classes and aria-invalid from all fields
  const errorFields = form.querySelectorAll('.input-field--error, [aria-invalid="true"]');
  errorFields.forEach((field) => {
    field.classList.remove('input-field--error');
    field.removeAttribute('aria-invalid');
  });

  // Handle radio/checkbox groups (gender field)
  const errorGroups = form.querySelectorAll('.input-group--error');
  errorGroups.forEach((group) => {
    group.classList.remove('input-group--error');
  });

  // Clear error state from all dob-input components
  const dobInputs = form.querySelectorAll('dob-input');
  dobInputs.forEach((input) => {
    input.removeAttribute('error');
    input.removeAttribute('aria-invalid');
  });

  // Clear error summary
  if (errorSummary) {
    errorSummary.textContent = '';
    errorSummary.classList.remove('error-summary--visible');
  }
}

function displayErrors(
  form: HTMLFormElement,
  errors: ValidationErrors,
  errorSummary: HTMLElement | null
): void {
  const errorMessages: string[] = [];

  for (const [fieldName, message] of Object.entries(errors)) {
    errorMessages.push(message);

    // Handle special case for gender (radio buttons)
    if (fieldName === 'gender') {
      const genderInputs = form.querySelectorAll<HTMLInputElement>('input[name="gender"]');
      genderInputs.forEach((input) => {
        input.setAttribute('aria-invalid', 'true');
      });
      // Find the parent input-group and add error class
      const firstGenderInput = genderInputs[0];
      if (firstGenderInput) {
        const inputGroup = firstGenderInput.closest('.input-group');
        if (inputGroup) {
          inputGroup.classList.add('input-group--error');
        }
      }
      continue;
    }

    // Handle birthdate (custom dob-input component)
    if (fieldName === 'birthdate') {
      const dobInput = form.querySelector<HTMLElement>('dob-input[name="birthdate"]');
      if (dobInput) {
        dobInput.setAttribute('aria-invalid', 'true');
        dobInput.setAttribute('error', 'true');
      }
      continue;
    }

    // Standard input fields
    const field = form.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      `[name="${fieldName}"]`
    );
    if (field) {
      field.classList.add('input-field--error');
      field.setAttribute('aria-invalid', 'true');
    }
  }

  // Display error summary
  if (errorSummary && errorMessages.length > 0) {
    const summaryIntro = form.dataset.errorSummaryIntro ?? 'Please correct the following errors:';
    const summaryText =
      errorMessages.length === 1
        ? errorMessages[0]
        : `${summaryIntro}\n• ${errorMessages.join('\n• ')}`;

    errorSummary.textContent = summaryText;
    errorSummary.classList.add('error-summary--visible');
    errorSummary.focus();
  }
}

function showSuccessMessage(form: HTMLFormElement, message: string): void {
  // Hide the form content
  const formGroup = form.querySelector<HTMLElement>('.form-group');
  const submitButton = form.querySelector<HTMLElement>('.submit-button');
  if (formGroup) formGroup.style.display = 'none';
  if (submitButton) submitButton.style.display = 'none';

  // Show success message
  const errorSummary = document.getElementById('error-summary');
  if (errorSummary) {
    errorSummary.textContent = message;
    errorSummary.classList.remove('error-summary--visible');
    errorSummary.classList.add('success-message--visible');
    errorSummary.focus();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTrialSignupForm);
} else {
  initTrialSignupForm();
}
